import {memo, forwardRef} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChevronRight} from "@fortawesome/sharp-regular-svg-icons/faChevronRight";
import Link from "next/link";
import clsMerge from "../../../util/clsMerge";
import {CollapsibleMenu, MenuItem, MenuItemBase} from "./MenuTypes";
import useArrowKeyNavigation, {OrientationMap} from "./useArrowKeyNavigation";
import {useUserInfo} from "../../contexts/userInfoContext";
import {FCWithChildren} from "@/app/types/reactTypes";
import RoleRestrictedView from "../RoleRestrictedView";
import {useMobileMenuItemContext} from "./MobileMenuItemContext";
import {useIsHrefCurrentPath} from "@/app/util/clientUrlUtils";

const MobileMenuItemPermissionsWrapper: FCWithChildren<
	Pick<MenuItem, "requireLogin" | "visibleForRoles" | "hiddenForRoles">
> = ({children, requireLogin, visibleForRoles, hiddenForRoles}) => {
	const {userInfo} = useUserInfo();

	if (requireLogin && !userInfo) {
		return;
	}

	return (
		<RoleRestrictedView
			visibleForRoles={visibleForRoles}
			hiddenForRoles={hiddenForRoles}
		>
			{children}
		</RoleRestrictedView>
	);
};

export interface MobileMenuItemProps extends MenuItemBase {
	className?: string;
	key?: string;
	href?: string;
	isExpanded?: boolean;
	menuItems?: MenuItem[];
	openSubmenu: (submenu: CollapsibleMenu) => void;
}

/**
 * Create a menu item.
 *
 * NOTE: I could think of no other way to do this and also make typescript happy.
 * @param props a destructured MenuLink or CollapsibleMenu
 * @returns a menu item for a menu.
 */
const MobileMenuItem = forwardRef<
	HTMLAnchorElement | HTMLButtonElement,
	MobileMenuItemProps
>(function MobileMenuItem(props, ref) {
	const {
		className,
		href,
		id,
		label,
		menuItems,
		isExpanded,
		openSubmenu,
		requireLogin,
		visibleForRoles,
		hiddenForRoles,
	} = props;

	const mobileMenuItemContext = useMobileMenuItemContext();
	const isHrefCurrentLocation = useIsHrefCurrentPath(href);
	const handleUpDownEvent = useArrowKeyNavigation({
		orientation: OrientationMap.VERTICAL,
	});

	const submenuButtonKeyDown = (
		e: React.KeyboardEvent<HTMLButtonElement | HTMLAnchorElement>
	) => {
		switch (e.key) {
			case "ArrowRight":
				if (menuItems) {
					openSubmenu({id, label, menuItems});
				}
				break;
			default:
				handleUpDownEvent(e);
		}
	};

	/**
	 *  TODO: consider using an HOC or some other methodology for assigning these
	 *        styles.  As the back button is also styled separately.
	 *        Also if we could have separate components representing a Link
	 *       and a button that would be the correct way to do this. :')
	 **/
	const menuItemStyles = clsMerge(
		"min-h-[3rem] py-2 px-3 flex items-center rounded-full focus:bg-dark-green focus:text-pale-lime-green focus-visible:outline-none transition-colors md:px-6 md:justify-center",
		className
	);

	// create a menu item if it's a link
	if (href && !menuItems) {
		return (
			<MobileMenuItemPermissionsWrapper
				requireLogin={requireLogin}
				visibleForRoles={visibleForRoles}
				hiddenForRoles={hiddenForRoles}
			>
				<Link
					href={href}
					className={menuItemStyles}
					ref={ref as React.Ref<HTMLAnchorElement>}
					role="menuitem"
					onKeyDown={(e) => handleUpDownEvent(e)}
					onMouseEnter={(e) => {
						(e.target as HTMLAnchorElement).focus();
					}}
					onClick={(e) => {
						if (isHrefCurrentLocation) {
							mobileMenuItemContext?.onNavigateToCurrentPage?.(e);
						}
					}}
				>
					{label}
				</Link>
			</MobileMenuItemPermissionsWrapper>
		);
	}

	// create a submenu if it's a submenu
	if (menuItems) {
		return (
			<MobileMenuItemPermissionsWrapper
				requireLogin={requireLogin}
				visibleForRoles={visibleForRoles}
				hiddenForRoles={hiddenForRoles}
			>
				<button
					className={clsMerge(menuItemStyles, "gap-2")}
					id={`${id}-submenu-button`}
					onClick={() => {
						openSubmenu({id, label, menuItems});
					}}
					onKeyDown={submenuButtonKeyDown}
					onMouseEnter={(e) => {
						(e.target as HTMLButtonElement).focus();
					}}
					ref={ref as React.Ref<HTMLButtonElement>}
					role="menuitem"
					aria-haspopup="menu"
					aria-expanded={isExpanded}
				>
					{label}
					<FontAwesomeIcon icon={faChevronRight} />
				</button>
			</MobileMenuItemPermissionsWrapper>
		);
	}

	console.warn("Could not determine item type");
	return <></>;
});

export default memo(MobileMenuItem);
